import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Container, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { debounce } from 'lodash';
import { DataRetriever } from '../../api/DataRetriever';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import AppSearch from '../../components/common/AppSearch';
import { DataspaceConnector } from '../../models/DataSpace';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import DataSpaceConnectorForm from '../../components/forms/DataSpaceConnectorForm';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';


export default function DataspaceConnectorsList() {
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [connector, setConnector] = useState<DataspaceConnector>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const filteredConnectors = useMemo(() => connectors?.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase())), [connectors, searchText]);
  const sectionLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "dataspace-connectors"));

  const initConnectors = useCallback(() => {
    DataRetriever.getDataspaceConnectors()
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `${sectionLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
    // eslint-disable-next-line
  }, [showToast]);

  useEffect(() => initConnectors(), [initConnectors]);

  const reset = useCallback(() => {
    setConnectors(undefined);
    initConnectors();
  }, [initConnectors]);

  const handleClose = useCallback(() => {
    setShowEditModal(false)
    setShowDeleteModal(false);
    setConnector(undefined);
    setEditing(undefined);
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleEdit = useCallback((conn: DataspaceConnector, index: number) => () => {
    setEditing(index);
    setConnector(conn);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback((conn: DataspaceConnector) => () => {
    setConnector(conn);
    setShowDeleteModal(true);
  }, []);

  const handleSubmit = useCallback((values: DataspaceConnector) => {
    showLoader();
    const promise = editing !== undefined
      ? DataRetriever.editDataspaceConnector(values!)
      : DataRetriever.createDataspaceConnector(values!);

    promise.then(
      () => {
        hideLoader();
        showToast('connector-success', `${sectionLabels.singular} ${editing !== undefined ? 'updated' : 'created'} successfully`, `${sectionLabels.singular} ${editing !== undefined ? 'Updated' : 'Created'}`, 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connector-error', message, `${sectionLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [handleClose, showLoader, hideLoader, reset, showToast, editing, sectionLabels]);

  const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const deleteConnector = useCallback(() => {
    showLoader();

    DataRetriever.deleteDataspaceConnnector(connector!.id)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('connector-success', `${sectionLabels.singular} deleted successfully`, `${sectionLabels.singular} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connector-error', message, `${sectionLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, connector, reset, showToast, sectionLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!filteredConnectors) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return filteredConnectors?.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {sectionLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {filteredConnectors!.map((connector, index) => (
            <Col key={connector.id} md={3} className="px-2 mb-3">
              <DataspaceConnectorItem
                className="h-100"
                connector={connector}
                onEdit={handleEdit(connector, index)}
                onDelete={handleDelete(connector)}
              />
            </Col>
          ))}
        </Row>
      )
  }, [error, filteredConnectors, handleDelete, handleEdit, sectionLabels]);

  return (
    <Container className="mt-3">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-0">{sectionLabels.plural} List</h4>
        </Col>

        <Col>
          <AppSearch placeholder={`Search ${sectionLabels.singular}...`} onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {sectionLabels.singular}</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? 'Edit' : 'New'} {sectionLabels.singular}</Modal.Title>
        </Modal.Header>
        <DataSpaceConnectorForm connector={connector} onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {sectionLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {sectionLabels.singular} <b>{connector?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteConnector}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}